import { Button } from "@/project/karuna/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Shield, School, ArrowRight } from "lucide-react";
import { useToast } from "@/project/karuna/hooks/use-toast";
import { useState } from "react";
import navigationRoutes, {
  useKarunaLink,
} from "@/project/karuna/utils/navigationRoutes";

const DemoLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState<string | null>(null);
  const getLink = useKarunaLink();
  const handleQuickLogin = async (role: "admin" | "school" | "evaluator") => {
    setLoading(role);

    const credentials = {
      admin: { email: "admin@gmail.com", label: "Administrator" },
      school: { email: "school@demo.com", label: "School" },
      evaluator: { email: "evaluator@demo.com", label: "Evaluator" },
    };

    const { email, label } = credentials[role];

    // Step 1: Show authenticating
    toast({
      title: "Step 1: Authenticating",
      description: `Verifying credentials for ${email}...`,
    });
    await new Promise(resolve => setTimeout(resolve, 800));

    // Step 2: Loading profile
    toast({
      title: "Step 2: Loading Profile",
      description: "Fetching user data and permissions...",
    });
    await new Promise(resolve => setTimeout(resolve, 800));

    // Step 3: Setting up workspace
    toast({
      title: "Step 3: Setting Up Workspace",
      description: `Preparing ${label} portal with demo data...`,
    });
    await new Promise(resolve => setTimeout(resolve, 800));

    // Step 4: Success
    toast({
      title: "✓ Login Successful",
      description: `Welcome! Opening ${label} portal...`,
    });
    await new Promise(resolve => setTimeout(resolve, 600));

    // Navigate to the appropriate portal
    navigate(
      role === "admin"
        ? getLink("/admin/dashboard")
        : role === "evaluator"
          ? getLink("/evaluator/dashboard")
          : getLink("/school/dashboard")
    );

    setLoading(null);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background p-4">
      <div className="w-full max-w-5xl space-y-8">
        <div className="space-y-2 text-center">
          <h1 className="text-4xl font-bold">Karuna</h1>
          <p className="text-lg text-muted-foreground">
            Choose your role to explore the platform
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {/* Admin Login Card */}
          <Card className="group relative overflow-hidden border-2 transition-all hover:shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10 opacity-0 transition-opacity group-hover:opacity-100" />
            <CardHeader className="relative">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/70">
                <Shield className="h-8 w-8 text-primary-foreground" />
              </div>
              <CardTitle className="text-center text-2xl">
                Admin Portal
              </CardTitle>
              <CardDescription className="text-center">
                Full administrative access to manage schools, activities, and
                donations
              </CardDescription>
            </CardHeader>
            <CardContent className="relative space-y-4">
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  <span>Manage 2,500+ schools</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  <span>Review activity submissions</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  <span>Generate reports & leaderboards</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                  <span>Track donations & receipts</span>
                </div>
              </div>

              <div className="space-y-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => navigate(getLink("/admin/login"))}
                  className="w-full"
                  disabled={loading !== null}
                >
                  Login
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* School Login Card */}
          <Card className="group relative overflow-hidden border-2 transition-all hover:shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-emerald-500/10 opacity-0 transition-opacity group-hover:opacity-100" />
            <CardHeader className="relative">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-emerald-600">
                <School className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-center text-2xl">
                School Portal
              </CardTitle>
              <CardDescription className="text-center">
                Access your school dashboard to manage activities and
                submissions
              </CardDescription>
            </CardHeader>
            <CardContent className="relative space-y-4">
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  <span>View assigned activities</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  <span>Submit activity reports</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  <span>Track submission status</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                  <span>Manage school profile</span>
                </div>
              </div>

              <div className="space-y-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => navigate(getLink("/school/auth"))}
                  className="w-full"
                  disabled={loading !== null}
                >
                  Login / Sign Up
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Evaluator Login Card */}
          <Card className="group relative overflow-hidden border-2 transition-all hover:shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/10 opacity-0 transition-opacity group-hover:opacity-100" />
            <CardHeader className="relative">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-600">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-center text-2xl">
                Evaluator Portal
              </CardTitle>
              <CardDescription className="text-center">
                Review and score school activity submissions with evaluation
                tools
              </CardDescription>
            </CardHeader>
            <CardContent className="relative space-y-4">
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                  <span>Review all submissions</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                  <span>Score & grade activities</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                  <span>View leaderboards</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                  <span>Track school performance</span>
                </div>
              </div>

              <div className="space-y-3 pt-4">
                <Button
                  onClick={() => handleQuickLogin("evaluator")}
                  disabled={loading !== null}
                  className="group w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
                  size="lg"
                >
                  {loading === "evaluator" ? (
                    "Logging in..."
                  ) : (
                    <>
                      Quick Login as Evaluator
                      <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </>
                  )}
                </Button>
              </div>

              <div className="space-y-1 pt-2 text-center text-xs text-muted-foreground">
                <p className="font-mono">evaluator@demo.com</p>
                <p className="font-mono">Password: 123456</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="text-center">
          <Button
            variant="ghost"
            onClick={() => navigate(getLink(navigationRoutes.HOME))}
            className="text-muted-foreground"
          >
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DemoLogin;
